import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
// html2canvas loaded dynamically on capture to reduce bundle size
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Download, Loader2, Share2, RectangleVertical, Square, X } from 'lucide-react';
import { Tooltip as UITooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import { toast } from 'sonner';
import { useNativeShare } from '../hooks/useNativeShare';
import { getDisplayName } from '../lib/displayName';

const FONT = "'Barlow Condensed', sans-serif";

const RATIOS = {
  story:  { w: 1080, h: 1920, label: '9:16', previewW: 200, previewH: 356, cssAspect: '9/16' },
  square: { w: 1080, h: 1080, label: '1:1',  previewW: 240, previewH: 240, cssAspect: '1/1' },
};

const BG_OPTIONS = [
  { id: 'dark',        label: 'Dark',   bg: '#1a1a1a',   text: '#ffffff', logoPrimary: '#FF5C00', logoSecondary: '#ffffff' },
  { id: 'black',       label: 'Black',  bg: '#000000',   text: '#ffffff', logoPrimary: '#FF5C00', logoSecondary: '#ffffff' },
  { id: 'orange',      label: 'Orange', bg: '#FF5C00',   text: '#ffffff', logoPrimary: '#1a1a1a', logoSecondary: '#ffffff' },
  { id: 'navy',        label: 'Navy',   bg: '#0f172a',   text: '#ffffff', logoPrimary: '#FF5C00', logoSecondary: '#ffffff' },
  { id: 'white',       label: 'White',  bg: '#ffffff',   text: '#1a1a1a', logoPrimary: '#FF5C00', logoSecondary: '#1a1a1a' },
  { id: 'transparent', label: 'None',   bg: 'transparent', text: '#ffffff', logoPrimary: '#FF5C00', logoSecondary: '#ffffff' },
];

const POST_TYPE_CONFIGS = {
  meal: {
    icon: '\uD83C\uDF7D\uFE0F',
    labelKey: 'storyShare.postTypeMeal',
    labelFallback: 'Meal',
    gradientFrom: '#FF5C00',
    gradientTo: '#ff8c42',
    accent: '#FF5C00',
  },
  diet_day: {
    icon: '\uD83D\uDCCA',
    labelKey: 'storyShare.postTypeNutritionDay',
    labelFallback: 'Nutrition Day',
    gradientFrom: '#22c55e',
    gradientTo: '#4ade80',
    accent: '#22c55e',
  },
  checkin: {
    icon: '\uD83D\uDCF8',
    labelKey: 'storyShare.postTypeCheckIn',
    labelFallback: 'Check-In',
    gradientFrom: '#8b5cf6',
    gradientTo: '#a78bfa',
    accent: '#8b5cf6',
  },
  workout: {
    icon: '\uD83D\uDCAA',
    labelKey: 'storyShare.postTypeWorkout',
    labelFallback: 'Workout',
    gradientFrom: '#ef4444',
    gradientTo: '#f87171',
    accent: '#ef4444',
  },
};

function MacroBar({ label, value, unit, color, previewW, textColor }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: previewW * 0.025 }}>
      <div style={{
        width: previewW * 0.022,
        height: previewW * 0.022,
        borderRadius: '50%',
        background: color,
        flexShrink: 0,
      }} />
      <div style={{ fontSize: previewW * 0.048, color: `${textColor}90`, flex: 1, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: previewW * 0.052, fontWeight: 800, color }}>{value}{unit}</div>
    </div>
  );
}

function CardContent({ theme, post, postType, previewW, previewH, t }) {
  const config = POST_TYPE_CONFIGS[postType] || POST_TYPE_CONFIGS.meal;
  const T = typeof t === 'function' ? t : (_k, f) => f;
  const configLabel = T(config.labelKey, config.labelFallback);
  const pad = previewW * 0.05;

  // Extract macros based on post type
  const getMacros = () => {
    if (postType === 'diet_day') {
      return {
        calories: post.day_calories || 0,
        protein: post.day_protein || 0,
        carbs: post.day_carbs || 0,
        fat: post.day_fat || 0,
      };
    }
    // meal type or fallback
    return {
      calories: post.meal_calories || post.calories || 0,
      protein: post.meal_protein || post.protein || 0,
      carbs: post.meal_carbs || post.carbs || 0,
      fat: post.meal_fat || post.fat || 0,
    };
  };

  const macros = getMacros();
  const hasMacros = macros.calories > 0 || macros.protein > 0;

  // Check-in data
  const checkinPhotos = post.checkin_photos || post.photos || [];
  const checkinWeight = post.weight || post.checkin_weight;
  const checkinMood = post.mood || post.checkin_mood;

  // Meal photo
  const mealPhoto = post.meal_photo || post.photo_url || post.image_url;

  // Caption
  const caption = post.content || post.caption || '';

  // Username
  const userName = getDisplayName(post.user_name || post.username || '');

  // Meal label
  const mealLabel = post.meal_label || post.meal_type || '';

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: pad,
      fontFamily: FONT,
      boxSizing: 'border-box',
    }}>
      {/* Header: branding + type badge */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: pad * 0.6,
      }}>
        <div style={{ fontWeight: 900, fontSize: previewW * 0.085, letterSpacing: 1 }}>
          <span style={{ color: theme.logoPrimary || '#FF5C00' }}>trained</span>
          <span style={{ color: theme.logoSecondary || theme.text }}>byRS</span>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: previewW * 0.015,
          background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
          padding: `${previewW * 0.012}px ${previewW * 0.03}px`,
          borderRadius: previewW * 0.02,
        }}>
          <span style={{ fontSize: previewW * 0.035 }}>{config.icon}</span>
          <span style={{
            fontSize: previewW * 0.032,
            fontWeight: 700,
            color: '#ffffff',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}>{configLabel}</span>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: `${theme.text}20`, marginBottom: pad * 0.5 }} />

      {/* Main content area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: pad * 0.4, overflow: 'hidden' }}>

        {/* Meal photo */}
        {(postType === 'meal' || postType === 'diet_day') && mealPhoto && (
          <div style={{
            width: '100%',
            flex: 1,
            minHeight: previewH * 0.35,
            borderRadius: previewW * 0.03,
            overflow: 'hidden',
          }}>
            <img
              src={mealPhoto}
              alt=""
              crossOrigin="anonymous"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        )}

        {/* Meal label */}
        {postType === 'meal' && mealLabel && (
          <div style={{
            fontSize: previewW * 0.045,
            fontWeight: 700,
            color: config.accent,
            textTransform: 'uppercase',
            letterSpacing: 1,
          }}>
            {mealLabel}
          </div>
        )}

        {/* Macros for meal/diet_day */}
        {(postType === 'meal' || postType === 'diet_day') && hasMacros && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: pad * 0.35,
            background: `${theme.text}12`,
            borderRadius: previewW * 0.03,
            padding: pad * 0.6,
            flexShrink: 0,
          }}>
            {/* Calories header */}
            <div style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: previewW * 0.02,
              marginBottom: pad * 0.15,
            }}>
              <span style={{
                fontSize: previewW * 0.08,
                fontWeight: 900,
                color: config.accent,
                lineHeight: 1,
              }}>{Math.round(macros.calories)}</span>
              <span style={{
                fontSize: previewW * 0.035,
                color: `${theme.text}60`,
                fontWeight: 600,
              }}>kcal</span>
            </div>

            <MacroBar label="Protein" value={Math.round(macros.protein)} unit="g" color="#ef4444" previewW={previewW} textColor={theme.text} />
            <MacroBar label="Carbs" value={Math.round(macros.carbs)} unit="g" color="#f59e0b" previewW={previewW} textColor={theme.text} />
            <MacroBar label="Fat" value={Math.round(macros.fat)} unit="g" color="#3b82f6" previewW={previewW} textColor={theme.text} />
          </div>
        )}

        {/* Check-in photos */}
        {postType === 'checkin' && checkinPhotos.length > 0 && (
          <div style={{
            display: 'flex',
            gap: previewW * 0.02,
            height: previewH * 0.4,
            flexShrink: 0,
          }}>
            {checkinPhotos.slice(0, 3).map((photo, i) => (
              <div key={i} style={{
                flex: 1,
                borderRadius: previewW * 0.025,
                overflow: 'hidden',
              }}>
                <img
                  src={typeof photo === 'string' ? photo : photo.url || photo.image_url}
                  alt=""
                  crossOrigin="anonymous"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Check-in stats (weight, mood) */}
        {postType === 'checkin' && (checkinWeight || checkinMood) && (
          <div style={{
            display: 'flex',
            gap: previewW * 0.03,
            flexWrap: 'wrap',
          }}>
            {checkinWeight && (
              <div style={{
                background: `${config.accent}20`,
                borderRadius: previewW * 0.02,
                padding: `${previewW * 0.015}px ${previewW * 0.03}px`,
                display: 'flex',
                alignItems: 'center',
                gap: previewW * 0.015,
              }}>
                <span style={{ fontSize: previewW * 0.04 }}>{'\u2696\uFE0F'}</span>
                <span style={{
                  fontSize: previewW * 0.042,
                  fontWeight: 700,
                  color: config.accent,
                }}>{checkinWeight} kg</span>
              </div>
            )}
            {checkinMood && (
              <div style={{
                background: `${config.accent}20`,
                borderRadius: previewW * 0.02,
                padding: `${previewW * 0.015}px ${previewW * 0.03}px`,
                display: 'flex',
                alignItems: 'center',
                gap: previewW * 0.015,
              }}>
                <span style={{ fontSize: previewW * 0.04 }}>{'\uD83D\uDE0A'}</span>
                <span style={{
                  fontSize: previewW * 0.042,
                  fontWeight: 700,
                  color: config.accent,
                }}>{checkinMood}</span>
              </div>
            )}
          </div>
        )}

        {/* Caption */}
        {caption && (
          <div style={{
            fontSize: previewW * 0.038,
            color: `${theme.text}90`,
            fontStyle: 'italic',
            lineHeight: 1.5,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
          }}>
            {caption}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: pad * 0.5,
        borderTop: `1px solid ${theme.text}20`,
        marginTop: pad * 0.3,
      }}>
        <div style={{ fontSize: previewW * 0.038, fontWeight: 600, color: `${theme.text}50` }}>
          trainedbyRS.com
        </div>
        {userName && (
          <div style={{ fontSize: previewW * 0.034, color: `${theme.text}50` }}>
            @{userName}
          </div>
        )}
      </div>
    </div>
  );
}

export default function GenericPostStoryGenerator({ open, onClose, post, postType }) {
  const { t } = useTranslation();
  const [ratio, setRatio] = useState('story');
  const [bg, setBg] = useState('dark');
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const slideRef = useRef(null);
  const { shareImage, downloadImage } = useNativeShare();

  if (!open || !post) return null;

  const r = RATIOS[ratio];
  const theme = BG_OPTIONS.find(o => o.id === bg) || BG_OPTIONS[0];

  const captureCanvas = async () => {
    if (!slideRef.current) throw new Error('No ref');
    const { default: html2canvas } = await import('html2canvas');
    // canvas captured at full export size with scale:2 for retina-quality export
    return html2canvas(slideRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: theme.bg === 'transparent' ? null : theme.bg,
      width: r.w,
      height: r.h,
    });
  };

  const filePrefix = postType || 'post';

  const handleExport = async () => {
    setExporting(true);
    try {
      const canvas = await captureCanvas();
      downloadImage(canvas, `trainedbyrs-${filePrefix}.png`);
      toast.success(t('social.storyDownloaded', 'Image saved!'));
    } catch {
      toast.error(t('social.storyExportFailed', 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const canvas = await captureCanvas();
      const result = await shareImage(canvas, {
        title: 'trainedbyRS',
        text: post.content || 'Check it out on trainedbyRS!',
        fileName: `trainedbyrs-${filePrefix}.png`,
        successMessage: t('social.storyDownloaded', 'Image downloaded — share it to your story!'),
        errorMessage: t('social.storyShareFailed', 'Failed to share image'),
      });
      if (result === 'shared') {
        toast.success(t('social.storyShared', 'Shared successfully!'));
      }
    } catch {
      toast.error(t('social.storyShareFailed', 'Failed to share'));
    } finally {
      setSharing(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] bg-black/90 flex flex-col items-center justify-start overflow-y-auto p-4 pb-8">
      <div className="w-full max-w-xs">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2
            className="text-white font-bold text-xl uppercase tracking-wide"
            style={{ fontFamily: FONT }}
          >
            {t('social.shareStory', 'Share Story')}
          </h2>
          <UITooltip>
            <TooltipTrigger asChild>
              <button onClick={onClose} className="text-white/60 hover:text-white p-1">
                <X className="h-6 w-6" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('common.close', 'Close')}</TooltipContent>
          </UITooltip>
        </div>

        {/* Ratio toggle */}
        <div className="flex gap-2 mb-3">
          {Object.entries(RATIOS).map(([key, val]) => (
            <button
              key={key}
              onClick={() => setRatio(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                ratio === key ? 'bg-primary text-white' : 'bg-white/10 text-white/60 hover:text-white'
              }`}
            >
              {key === 'story' ? <RectangleVertical className="h-3 w-3" /> : <Square className="h-3 w-3" />}
              {val.label}
            </button>
          ))}
        </div>

        {/* BG toggle */}
        <div className="flex gap-1.5 flex-wrap mb-5">
          {BG_OPTIONS.map(opt => (
            <button
              key={opt.id}
              onClick={() => setBg(opt.id)}
              className={`px-2.5 py-1 rounded text-xs font-medium border-2 transition-all ${
                bg === opt.id ? 'border-primary' : 'border-transparent'
              }`}
              style={{ background: opt.bg, color: opt.text }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Canvas preview — slide renders at full export size, CSS-scaled for display */}
        <div className="flex justify-center mb-5">
          <div
            style={{
              width: r.previewW,
              height: r.previewH,
              borderRadius: 12,
              overflow: 'hidden',
              flexShrink: 0,
              position: 'relative',
              backgroundImage: theme.bg === 'transparent'
                ? 'linear-gradient(45deg, #2a2a2a 25%, transparent 25%), linear-gradient(-45deg, #2a2a2a 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #2a2a2a 75%), linear-gradient(-45deg, transparent 75%, #2a2a2a 75%)'
                : undefined,
              backgroundSize: theme.bg === 'transparent' ? '16px 16px' : undefined,
              backgroundPosition: theme.bg === 'transparent' ? '0 0, 0 8px, 8px -8px, -8px 0px' : undefined,
              backgroundColor: theme.bg === 'transparent' ? '#1a1a1a' : undefined,
            }}
          >
            <div
              style={{
                transform: `scale(${r.previewW / r.w})`,
                transformOrigin: 'top left',
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              <div
                ref={slideRef}
                style={{
                  width: r.w,
                  height: r.h,
                  background: theme.bg === 'transparent' ? undefined : theme.bg,
                  overflow: 'hidden',
                }}
              >
                <CardContent
                  theme={theme}
                  post={post}
                  postType={postType}
                  previewW={r.w}
                  previewH={r.h}
                  t={t}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Hint text */}
        <p className="text-white/40 text-xs text-center mb-4">
          {t('social.shareStoryHint', 'Download the image and share to Instagram Stories, Facebook, or any app')}
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-white/20 text-white hover:bg-white/10"
            onClick={handleExport}
            disabled={exporting || sharing}
          >
            {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            {t('common.download', 'Download')}
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-[#e05200] text-white"
            onClick={handleShare}
            disabled={exporting || sharing}
          >
            {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />}
            {t('common.share', 'Share')}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
